import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { savedCustomGraphs as savedCustomGraphsApi } from '../../requests/api/apiClient';
import {
  ChartRenderer,
  ChartSerie,
  CustomGrapherTabProps,
  SavedGraph,
  SERIES,
  fetchSerie,
} from './customGrapher.helpers';

const CustomGrapherTab: React.FC<CustomGrapherTabProps> = ({
  farmId, sectorId,
  wiseconnId = '', weatherZoneId = '', sn = '', telemetryType = 'wiseconn',
}) => {
  const [startDate, setStartDate] = useState<Date | null>(dayjs().subtract(30, 'day').toDate());
  const [endDate, setEndDate] = useState<Date | null>(dayjs().toDate());
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const [cachedData, setCachedData] = useState<Record<string, ChartSerie[]>>({});
  const [selectedDepths, setSelectedDepths] = useState<Record<string, string[]>>({});
  const [savedGraphs, setSavedGraphs] = useState<SavedGraph[]>([]);
  const [savePanelOpen, setSavePanelOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [hasRenderError, setHasRenderError] = useState(false);

  const checkedRef = useRef<Set<string>>(checked);
  checkedRef.current = checked;

  useEffect(() => {
    if (!sectorId) return;
    savedCustomGraphsApi.getBySector(sectorId)
      .then((res: any) => {
        const graphs = res?.data?.savedGraphs;
        setSavedGraphs(Array.isArray(graphs) ? graphs.filter((g: SavedGraph) => g?.id) : []);
      })
      .catch(() => setSavedGraphs([]));
  }, [sectorId]);

  const buildCtx = useCallback(() => ({
    farmId,
    sectorId,
    startDate: dayjs(startDate ?? new Date()).toISOString(),
    endDate: dayjs(endDate ?? new Date()).toISOString(),
    wiseconnId,
    weatherZoneId,
    sn,
    telemetryType,
  }), [farmId, sectorId, startDate, endDate, wiseconnId, weatherZoneId, sn, telemetryType]);

  const fetchAndCache = useCallback(async (id: string) => {
    setLoadingIds((prev) => new Set(prev).add(id));
    try {
      const series = await fetchSerie(id, buildCtx());
      setCachedData((prev) => ({ ...prev, [id]: series }));
    } catch {
      setCachedData((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setLoadingIds((prev) => { const s = new Set(prev); s.delete(id); return s; });
    }
  }, [buildCtx]);

  useEffect(() => {
    const ids = [...checkedRef.current];
    if (ids.length === 0) return;
    setCachedData({});
    ids.forEach((id) => fetchAndCache(id));
  }, [buildCtx, refetchTrigger]);

  const handleToggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
        if (!cachedData[id]) fetchAndCache(id);
      }
      return next;
    });
  };

  const handleSaveGraph = async () => {
    if (!saveName.trim()) return;
    try {
      const res: any = await savedCustomGraphsApi.create(sectorId, {
        name: saveName.trim(),
        checked: [...checked],
        selectedDepths,
        startDate: startDate ? dayjs(startDate).toISOString() : null,
        endDate: endDate ? dayjs(endDate).toISOString() : null,
      });
      const savedGraph: SavedGraph | undefined = res?.data?.savedGraph;
      if (savedGraph?.id) {
        setSavedGraphs((prev) => [savedGraph, ...prev]);
      }
      setSaveName('');
      setSavePanelOpen(false);
    } catch {
      setSavePanelOpen(false);
      setSaveName('');
    }
  };

  const handleLoadGraph = (graph: SavedGraph) => {
    try {
      setChecked(new Set(Array.isArray(graph.checked) ? graph.checked : []));
      setSelectedDepths(graph.selectedDepths && typeof graph.selectedDepths === 'object' ? graph.selectedDepths : {});
      if (graph.startDate) {
        const d = new Date(graph.startDate);
        if (!Number.isNaN(d.getTime())) setStartDate(d);
      }
      if (graph.endDate) {
        const d = new Date(graph.endDate);
        if (!Number.isNaN(d.getTime())) setEndDate(d);
      }
      setCachedData({});
      setRefetchTrigger((t) => t + 1);
    } catch {
      // graph data malformed, skip
    }
  };

  const handleDeleteGraph = async (id: string) => {
    if (!id) return;
    setSavedGraphs((prev) => prev.filter((g) => g.id !== id));
    try {
      await savedCustomGraphsApi.delete(id);
    } catch {
      savedCustomGraphsApi.getBySector(sectorId)
        .then((res: any) => setSavedGraphs(res?.data?.savedGraphs || []))
        .catch(() => {});
    }
  };

  const chartOptions = useMemo((): Highcharts.Options => {
    const allSeries: ChartSerie[] = SERIES
      .filter((s) => checked.has(s.id))
      .flatMap((s) => {
        const data = cachedData[s.id] || [];
        if (s.requiresDepthSelection) {
          const depths = selectedDepths[s.id] || [];
          if (depths.length === 0) return [];
          return data.filter((d) => depths.includes(d.name));
        }
        return data;
      })
      .filter((s) => s && Array.isArray(s.data) && s.data.length > 0);

    const units = [...new Set(allSeries.map((s) => s.unit))];

    const yAxes: Highcharts.YAxisOptions[] = units.length > 0
      ? units.map((unit, i) => ({
        title: { text: unit },
        opposite: i % 2 !== 0,
        gridLineWidth: i === 0 ? 1 : 0,
      }))
      : [{ title: { text: '' } }];

    const series: Highcharts.SeriesOptionsType[] = allSeries.map((s) => ({
      type: s.type as any,
      name: s.name,
      data: s.data,
      color: s.color,
      yAxis: Math.max(0, units.indexOf(s.unit)),
      zIndex: s.type === 'column' ? 1 : 2,
    }));

    return {
      chart: { zooming: { type: 'x' }, height: 480 },
      title: { text: undefined },
      xAxis: { type: 'datetime' },
      yAxis: yAxes,
      series,
      legend: { enabled: true },
      tooltip: { shared: false, xDateFormat: '%Y-%m-%d %H:%M' },
      credits: { enabled: false },
      plotOptions: {
        line: { marker: { enabled: false }, lineWidth: 2.5 },
        column: {
          grouping: false,
          pointPadding: 0.1,
          borderWidth: 0,
          pointRange: 86_400_000,
          opacity: 0.95,
        },
      },
    };
  }, [checked, cachedData, selectedDepths]);

  const hasData = SERIES.some((s) => {
    if (!checked.has(s.id)) return false;
    if (s.requiresDepthSelection) {
      const depths = selectedDepths[s.id] || [];
      if (depths.length === 0) return false;
      return cachedData[s.id]?.some((c) => depths.includes(c.name) && c.data.length > 0);
    }
    return cachedData[s.id]?.some((c) => c.data.length > 0);
  });
  const isAnyLoading = SERIES.some((s) => checked.has(s.id) && loadingIds.has(s.id));
  const showChart = hasData || (checked.size > 0 && isAnyLoading);

  const chartKey = useMemo(
    () => SERIES
      .filter((s) => {
        if (!checked.has(s.id)) return false;
        if (s.requiresDepthSelection) return (selectedDepths[s.id] || []).length > 0;
        return cachedData[s.id]?.some((c) => c.data.length > 0);
      })
      .map((s) => (s.requiresDepthSelection
        ? `${s.id}:${(selectedDepths[s.id] || []).join('|')}`
        : s.id))
      .join(','),
    [checked, cachedData, selectedDepths],
  );

  const groups = [...new Set(SERIES.map((s) => s.group))];

  if (hasRenderError) {
    return (
      <Box p={3}>
        <Typography color="error">
          Ocurrió un error al cargar el graficador. Por favor recarga la página.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Typography variant="h6" color="primary">
              Graficador personalizado
            </Typography>
            <Button
              variant="outlined"
              size="small"
              disabled={checked.size === 0}
              onClick={() => setSavePanelOpen((o) => !o)}
            >
              Guardar configuración
            </Button>
          </Box>

          {savePanelOpen && (
            <Box display="flex" alignItems="center" gap={1} mb={2} p={1.5} bgcolor="action.hover" borderRadius={1}>
              <TextField
                label="Nombre de la configuración"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                size="small"
                variant="outlined"
                sx={{ flexGrow: 1 }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveGraph(); }}
                autoFocus
              />
              <Button variant="contained" size="small" onClick={handleSaveGraph} disabled={!saveName.trim()}>
                Guardar
              </Button>
              <Button size="small" onClick={() => { setSavePanelOpen(false); setSaveName(''); }}>
                Cancelar
              </Button>
            </Box>
          )}

          {savedGraphs.length > 0 && (
            <Box mb={2}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                Configuraciones guardadas
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {savedGraphs
                  .filter((g): g is SavedGraph => Boolean(g?.id && g?.name))
                  .map((graph) => (
                    <Chip
                      key={graph.id}
                      label={graph.name}
                      size="small"
                      variant="outlined"
                      onClick={() => handleLoadGraph(graph)}
                      onDelete={() => handleDeleteGraph(graph.id)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
              </Box>
            </Box>
          )}

          <Grid container spacing={2} alignItems="flex-end" mb={2}>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="Fecha inicio"
                value={startDate}
                onChange={(d) => setStartDate(d)}
                slotProps={{ textField: { fullWidth: true, variant: 'standard' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <DatePicker
                label="Fecha fin"
                value={endDate}
                onChange={(d) => setEndDate(d)}
                slotProps={{ textField: { fullWidth: true, variant: 'standard' } }}
              />
            </Grid>
          </Grid>

          {groups.map((group) => (
            <Box key={group} mb={1}>
              <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
                {group}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                {SERIES.filter((s) => s.group === group).map((serie) => {
                  const isChecked = checked.has(serie.id);
                  const isLoading = loadingIds.has(serie.id);
                  const isWeatherDisabled = !!serie.requiresWeatherZone && !weatherZoneId;
                  const isDisabled = isLoading || isWeatherDisabled;
                  const depthSeries = serie.requiresDepthSelection
                    ? (cachedData[serie.id] || []) : [];
                  const showDepthChips = isChecked && !isLoading && depthSeries.length > 0;
                  const activeDepths = selectedDepths[serie.id] || [];
                  return (
                    <Box key={serie.id} display="flex" alignItems="center" flexWrap="wrap" gap={0.5}>
                      <Tooltip
                        title={isWeatherDisabled ? 'Este sector no tiene zona climática configurada' : ''}
                        disableHoverListener={!isWeatherDisabled}
                      >
                        <span>
                          <FormControlLabel
                            control={(
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handleToggle(serie.id)}
                                size="small"
                                disabled={isDisabled}
                                sx={{ color: serie.color, '&.Mui-checked': { color: serie.color } }}
                                icon={isLoading ? <CircularProgress size={16} /> : undefined}
                                checkedIcon={isLoading ? <CircularProgress size={16} /> : undefined}
                              />
                            )}
                            label={<Typography variant="body2">{serie.label}</Typography>}
                            sx={{
                              border: '1px solid',
                              borderColor: isChecked ? serie.color : 'divider',
                              borderRadius: 1,
                              px: 1,
                              mr: 0,
                              transition: 'border-color 0.2s',
                              opacity: isWeatherDisabled ? 0.4 : 1,
                              cursor: isWeatherDisabled ? 'not-allowed' : 'default',
                            }}
                          />
                        </span>
                      </Tooltip>
                      {showDepthChips && depthSeries.map((ds) => {
                        const isActive = activeDepths.includes(ds.name);
                        return (
                          <Chip
                            key={ds.name}
                            label={ds.name}
                            size="small"
                            onClick={() => setSelectedDepths((prev) => {
                              const current = prev[serie.id] || [];
                              const next = isActive
                                ? current.filter((d) => d !== ds.name)
                                : [...current, ds.name];
                              return { ...prev, [serie.id]: next };
                            })}
                            sx={{
                              height: 24,
                              fontSize: '0.7rem',
                              borderColor: ds.color,
                              color: isActive ? '#fff' : ds.color,
                              bgcolor: isActive ? ds.color : 'transparent',
                              border: '1px solid',
                              cursor: 'pointer',
                              '&:hover': { opacity: 0.85 },
                            }}
                          />
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      {showChart && (
        <Box mt={2}>
          <Card>
            <CardContent>
              {isAnyLoading && !hasData ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={480}>
                  <CircularProgress />
                </Box>
              ) : (
                <ChartRenderer
                  highcharts={Highcharts}
                  options={chartOptions}
                  chartKey={chartKey}
                  onError={() => setHasRenderError(true)}
                />
              )}
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default CustomGrapherTab;
